import { ParsedUrlQuery } from 'querystring';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useParams, useSearchParams } from 'next/navigation';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import clean from '@utils/cleanObject';
import { ListingMetaViewModel } from '@components/views/types';
import { callService } from '@api/remote-call';
import { TourListViewModel } from '@api/v3';
import { getFormattedDate, paramsToObject } from '@components/views/utils';
export const useListingFetching = (initialData?: TourListViewModel) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const [isClientSide, setIsClientSide] = useState(false);

  const formattedDate = getFormattedDate(query);
  const fetchTourItems = useCallback(() => {
    return callService(
      { service: 'ToursService', method: 'listTours', version: 'v3' },
      clean({
        slug: params?.slug as string,
        sort: query.sort as string,
        keywords: query.keywords as string,
        priceMin: query.priceMin as string,
        priceMax: query.priceMax as string,
        durationMin: query.durationMin as string,
        durationMax: query.durationMax as string,
        page: query.page ? (query.page as string) : '1',
        size: query.size as string,
        region: query.region as string[],
        theme: query.theme as string[],
        feature: query.feature as string[],
        date: formattedDate,
        status: query.status as string,
        segment: query.segment as string[],
        departure: query.departure as string[],
        transportation: query.transportation as string[],
        specialPeriod: query.specialPeriod as string[],
        visa: query.visa as string[],
      })
    );
  }, [
    query,
    params?.slug,
    query.feature,
    query.category,
    query.theme,
    query.region,
    query.boardType,
    query.status,
    query.segment,
    query.departure,
    query.transportation,
    query.visa,
  ]);

  const queryClient = useQueryClient();
  useEffect(() => {
    setIsClientSide(false);
    if (initialData?.data?.length) {
      queryClient.setQueryData(['tour-listing', JSON.stringify(query)], {
        pages: [initialData],
        pageParams: [initialData?.meta?.currentPage ?? 0],
      });
    }
  }, [initialData, query, queryClient]);
  const {
    data,
    isError,
    isFetching,
    isInitialLoading: isLoading,
    hasNextPage,
    isSuccess: isListingSuccess,
    refetch,
  } = useInfiniteQuery(
    ['tour-listing', JSON.stringify(query)],
    () => fetchTourItems(),
    {
      getNextPageParam: (lastPage) => {
        if (
          (lastPage.meta?.currentPage ?? 0) < (lastPage.meta?.totalPages ?? 0)
        ) {
          return (lastPage.meta?.currentPage ?? 0) + 1;
        }
        return false;
      },
      initialData: initialData?.data.length
        ? {
            pages: [initialData],
            pageParams: [initialData?.meta?.currentPage ?? 0],
          }
        : undefined,
      enabled: Boolean(params?.slug && isClientSide),
    }
  );

  const meta = useMemo(() => {
    const meta = data?.pages?.[data?.pages?.length - 1]?.meta;
    let metaData: ListingMetaViewModel;
    if (meta) {
      metaData = { ...meta, hasNextPage: !!hasNextPage };
      return metaData;
    }
  }, [data, hasNextPage]);

  return {
    tours: data?.pages?.map((tours) => tours.data) || [],
    filters: data?.pages[0].filters,
    isError,
    isLoading,
    isFetching,
    refetch,
    isListingSuccess,
    meta,
  };
};
